import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Bell, LogOut, Menu, X, BarChart3 } from 'lucide-react';
import CreditBalance from './CreditBalance';

// Simple error boundary for mobile menu components
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Mobile menu component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div>Error loading component</div>;
    }
    return this.props.children;
  }
}

const GlobalNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, token } = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [lastQueueCount, setLastQueueCount] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [isFirstMessageFetch, setIsFirstMessageFetch] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const [isFirstNotificationFetch, setIsFirstNotificationFetch] = useState(true);
  const [debugError, setDebugError] = useState(null);

  // Debug token state
  useEffect(() => {
    console.log('🔍 GlobalNavigation - Token state:', token ? 'Present' : 'Missing');
    console.log('📍 Current path:', location.pathname);
  }, [token, location.pathname]);

  // Hide navigation completely only on login/register pages
  const hideOnPages = ['/login', '/register'];
  const shouldHide = hideOnPages.includes(location.pathname);
  
  // Fetch queue count - only when logged in
  useEffect(() => {
    if (shouldHide) return;
    if (!token) return;
    
    const fetchQueueCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const newCount = data.count || 0;
          
          // Play sound if count increased (someone joined queue)
          if (newCount > lastQueueCount && lastQueueCount > 0) {
            playQueueNotificationSound();
          }
          
          setLastQueueCount(newCount);
          setQueueCount(newCount);
        }
      } catch (error) {
        console.error('Error fetching queue count:', error);
      }
    };
    
    fetchQueueCount();
    // Refresh every 5 seconds
    const interval = setInterval(fetchQueueCount, 5000);
    return () => clearInterval(interval);
  }, [token, lastQueueCount, shouldHide]);
  
  // Fetch notification counts for matches, messages, and notifications
  useEffect(() => {
    if (shouldHide) return;
    if (!token) return;
    
    const fetchNotificationCounts = async () => {
      try {
        // Fetch new matches count
        const matchesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (matchesResponse.ok) {
          const matchesData = await matchesResponse.json();
          // Count matches that haven't been viewed yet (you may need to add a 'viewed' field)
          const newMatches = matchesData.matches?.filter(m => !m.viewed).length || 0;
          setMatchCount(newMatches);
        }
        
        // Fetch unread messages count
        const messagesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/unread-count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (messagesResponse.ok) {
          const messagesData = await messagesResponse.json();
          const newMessageCount = messagesData.unread_count || 0;
          
          // Play sound if new messages arrived (but not on initial load)
          if (newMessageCount > lastMessageCount && !isFirstMessageFetch) {
            console.log('💬 New message detected, playing sound');
            playMessageSound();
          }
          
          // Mark first fetch as complete
          if (isFirstMessageFetch) {
            setIsFirstMessageFetch(false);
          }
          
          setLastMessageCount(newMessageCount);
          setMessageCount(newMessageCount);
        }
        
        // Fetch notifications count
        const notificationsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (notificationsResponse.ok) {
          const notificationsData = await notificationsResponse.json();
          const newNotificationCount = notificationsData.unread_count || 0;
          
          // Play sound if new notifications arrived (but not on initial load)
          if (newNotificationCount > lastNotificationCount && !isFirstNotificationFetch) {
            console.log('🔔 New notification detected, playing sound');
            playNotificationSound();
          }
          
          // Mark first fetch as complete
          if (isFirstNotificationFetch) {
            setIsFirstNotificationFetch(false);
          }
          
          setLastNotificationCount(newNotificationCount);
          setNotificationCount(newNotificationCount);
        }
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      }
    };
    
    fetchNotificationCounts();
    // Refresh every 10 seconds
    const interval = setInterval(fetchNotificationCounts, 10000);
    return () => clearInterval(interval);
  }, [token, shouldHide]);
  
  // Play notification sound (queue notification - single ping)
  const playQueueNotificationSound = () => {
    try {
      // Create audio context for ping sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configure ping sound (800Hz, 100ms)
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
      
      console.log('🔔 Queue notification sound played');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Play notification sound (general notification - double chime)
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // First tone (higher pitch)
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.frequency.value = 880; // A5 note
      oscillator1.type = 'sine';
      
      gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.15);
      
      // Second tone (slightly lower pitch, delayed)
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.value = 660; // E5 note
        oscillator2.type = 'sine';
        
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.2);
      }, 150);
      
      console.log('🔔 Notification sound played');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Play message notification sound (triple ascending tones)
  const playMessageSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const frequencies = [523, 659, 784]; // C5, E5, G5 (major chord)
      
      frequencies.forEach((freq, index) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = freq;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.25, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.15);
        }, index * 100);
      });
      
      console.log('💬 Message sound played');
    } catch (error) {
      console.error('Error playing message sound:', error);
    }
  };

  // Don't render if should be hidden
  if (shouldHide) {
    return null;
  }

  // If user is not logged in, show simple navigation with Login/Sign Up
  if (!token) {
    return (
      <>
        {/* Simple Navigation for Logged-Out Users */}
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-white/5 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex justify-between items-center">
              {/* Left side - Empty to match logged-in navigation */}
              <div className="w-10"></div>
              
              {/* Login and Sign Up Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-lg transition-all text-sm font-medium shadow-lg"
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Logged-in user navigation
  return (
    <>
      {/* Debug Error Display */}
      {debugError && (
        <div className="fixed top-0 left-0 right-0 bg-red-500 text-white p-4 z-[10000] text-center">
          <strong>Debug Error:</strong> {debugError}
          <button 
            onClick={() => setDebugError(null)}
            className="ml-4 bg-white text-red-500 px-3 py-1 rounded"
          >
            Close
          </button>
        </div>
      )}
      
      {/* Global Navigation Header - Matches Homepage Aesthetic */}
      <div className="fixed top-0 left-0 right-0 z-[9998] bg-white/5 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
          <div className="flex justify-between items-center">
            {/* Left side - Empty */}
            <div className="w-10"></div>
            
            {/* Right side - Hamburger menu on mobile/tablet, Desktop navigation on desktop */}
            <div className="flex items-center space-x-4">
              {/* Hamburger menu - shown on mobile/tablet, hidden on desktop */}
              <div className="xl:hidden">
                <button
                  onClick={() => {
                    try {
                      console.log('📱 Mobile menu toggle clicked, current state:', isMobileMenuOpen);
                      console.log('📍 Current page:', location.pathname);
                      setDebugError(null); // Clear previous errors
                      setIsMobileMenuOpen(!isMobileMenuOpen);
                    } catch (error) {
                      console.error('❌ Error toggling mobile menu:', error);
                      setDebugError(`Menu error: ${error.message}`);
                      alert(`Error opening menu: ${error.message}\nPage: ${location.pathname}`);
                    }
                  }}
                  className="text-white/80 hover:text-rose-300 transition-colors p-2 rounded-lg hover:bg-white/5"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
              
              {/* Desktop navigation - hidden on mobile/tablet, shown on desktop 1280px+ */}
              <div className="hidden xl:flex items-center space-x-1">
                <button
                  onClick={() => navigate('/matches')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm relative"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Matches
                  {matchCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {matchCount}
                    </span>
                  )}
                </button>
                
                {/* Your Connections Button */}
                <button
                  onClick={() => navigate('/connections')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm relative"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Your Connections
                </button>
                
                <button
                  onClick={() => navigate('/chat')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm relative"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Messages
                  {messageCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {messageCount}
                    </span>
                  )}
                </button>
                
                {/* Queue Button with Badge */}
                <button
                  onClick={() => navigate('/queue')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm relative"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Queue
                  {queueCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {queueCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => navigate('/billing')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Billing
                </button>
                <button
                  onClick={() => navigate('/notifications')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all flex items-center space-x-1 text-sm font-medium shadow-sm relative"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  <Bell className="w-4 h-4" />
                  <span>Notifications</span>
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {notificationCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all flex items-center space-x-1 text-sm font-medium shadow-sm"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Analytics</span>
                </button>
                
                {/* Credit Balance Widget */}
                <div className="flex items-center ml-2">
                  <CreditBalance size="small" />
                </div>
                
                <button
                  onClick={() => navigate('/profile')}
                  className="px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  Profile
                </button>
                
                <button
                  onClick={() => logout(navigate)}
                  className="flex items-center space-x-1 px-3 py-2 text-white hover:text-rose-300 hover:bg-white/10 rounded-lg transition-all text-sm font-medium shadow-sm"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Slide-in from right with matching aesthetic */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9997] xl:hidden"
            onClick={() => {
              try {
                setIsMobileMenuOpen(false);
              } catch (error) {
                console.error('Error closing menu via overlay:', error);
                setDebugError(`Overlay close error: ${error.message}`);
              }
            }}
          ></div>
          
          {/* Menu Panel - Matches homepage black background */}
          <div className="fixed top-0 right-0 bottom-0 w-72 bg-black/95 backdrop-blur-xl border-l border-white/10 shadow-2xl z-[9999] xl:hidden overflow-y-auto">
            <div className="p-6 space-y-2">
              {/* Close button */}
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => {
                    try {
                      setIsMobileMenuOpen(false);
                    } catch (error) {
                      console.error('Error closing menu via X:', error);
                      setDebugError(`Close button error: ${error.message}`);
                    }
                  }}
                  className="text-white/80 hover:text-rose-300 transition-colors p-2 rounded-lg hover:bg-white/5"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Credit Balance - with error handling */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <ErrorBoundary fallback={
                  <div className="text-white/60 text-sm p-2 bg-red-500/20 rounded">
                    Credits temporarily unavailable
                  </div>
                }>
                  {token ? <CreditBalance size="medium" /> : <div className="text-white/60">Login to see credits</div>}
                </ErrorBoundary>
              </div>
              
              {/* Menu Items */}
              <button
                onClick={() => {
                  navigate('/matches');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center justify-between"
              >
                <span>Matches</span>
                {matchCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                    {matchCount}
                  </span>
                )}
              </button>
              
              {/* Your Connections Mobile */}
              <button
                onClick={() => {
                  navigate('/connections');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light"
              >
                <span>Your Connections</span>
              </button>
              
              <button
                onClick={() => {
                  navigate('/chat');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center justify-between"
              >
                <span>Messages</span>
                {messageCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                    {messageCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => {
                  navigate('/queue');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center justify-between"
              >
                <span>Queue</span>
                {queueCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                    {queueCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => {
                  navigate('/billing');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light"
              >
                Billing
              </button>
              
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center justify-between"
              >
                <div className="flex items-center space-x-2">
                  <Bell className="w-4 h-4" />
                  <span>Notifications</span>
                </div>
                {notificationCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                    {notificationCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center space-x-2"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics</span>
              </button>
              
              <button
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light"
              >
                Profile
              </button>
              
              <div className="h-px bg-white/10 my-4"></div>
              
              <button
                onClick={() => {
                  logout(navigate);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left text-white/80 hover:text-rose-300 hover:bg-white/5 transition-all py-3 px-4 rounded-lg font-light flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GlobalNavigation;
